import React from 'react';
import { Icon } from '@iconify/react';
import RagClientSelect from '../assistant/RagClientSelect';
import CommercialSelect from '../CommercialSelect';
import {
  AppButton,
  AppCheckbox,
  AppFormField,
  AppInput,
  AppSelect,
  AppTextarea,
} from '../ui';
import { DepotTypeBadge } from './DepotBadges';
import DepotStep from './DepotStep';
import {
  AUDIO_EXTENSIONS,
  DOCUMENT_EXTENSIONS,
  MAX_FILES,
  TYPE_RDV_OPTIONS,
  formatFileSize,
  getDriveClientLabel,
  getDriveClientValue,
} from './depotUtils';

/**
 * Formulaire de dépôt (étapes client / fichiers / envoi).
 * RagClientSelect conservé tel quel (ne pas fusionner avec AppAutocomplete).
 */
function DepotUploadForm({
  formCardRef,
  fileInputRef,
  clients,
  clientsLoading,
  creatingClient,
  clientFolderId,
  onClientFolderIdChange,
  onRefreshClients,
  onCreateClient,
  personnes,
  commercialId,
  onCommercialChange,
  pendingItems,
  uploading,
  uploadIndex,
  overallProgress,
  dragActive,
  onDragOver,
  onDragLeave,
  onDrop,
  onOpenFilePicker,
  onFileInputChange,
  onAddNotes,
  onUpdatePendingItem,
  onRemovePendingItem,
  onApplyDefaultClientToAll,
  onApplyDefaultCommercialToAll,
  onApplyCommercialSuggestion,
  typeRdv,
  extractionRequise,
  onExtractionChange,
  showPerFileClient,
  pendingFileCount,
  pendingFilesBytes,
  canSubmit,
  submitHint,
  onSubmit,
}) {
  const acceptExtensions = [...AUDIO_EXTENSIONS, ...DOCUMENT_EXTENSIONS];
  const hasDocument = pendingItems.some((item) => item.typeDepot === 'document');

  return (
    <div className="card depot-form-card" ref={formCardRef}>
      <div className="depot-guidance depot-guidance--header depot-guidance--audio">
        <Icon icon="mdi:information-outline" className="depot-guidance__icon" aria-hidden />
        <div>
          <p className="depot-guidance__title mb-0">Rien de technique à préparer</p>
          <p className="depot-guidance__text mb-0">
            Choisissez le client, ajoutez votre fichier (ou des notes), puis cliquez sur Envoyer. Le compte rendu se crée tout seul.
          </p>
        </div>
      </div>
      <div className="card-body p-20">
        <form onSubmit={onSubmit} className="depot-form">
          <DepotStep number="1" title="Pour quel client ?">
            <div className="depot-client-picker">
              <div className="depot-client-picker__select">
                <RagClientSelect
                  clients={clients}
                  value={clientFolderId}
                  onChange={onClientFolderIdChange}
                  loading={clientsLoading}
                  disabled={uploading}
                  creating={creatingClient}
                  allowAll={false}
                  allowCreate
                  onCreate={(nom) => onCreateClient(nom)}
                  label={pendingItems.length <= 1 ? 'Client' : 'Client pour les nouveaux fichiers'}
                  placeholder="Tapez le nom du client…"
                  getOptionValue={getDriveClientValue}
                  getOptionLabel={getDriveClientLabel}
                />
              </div>
              <span
                className="depot-icon-tip depot-icon-tip--end depot-client-picker__refresh"
                data-tooltip="Actualiser la liste Drive"
              >
                <AppButton
                  type="button"
                  size="sm"
                  variant="outline-secondary"
                  icon="mdi:refresh"
                  iconWidth={18}
                  className="depot-icon-btn"
                  aria-label="Actualiser la liste des clients Drive"
                  disabled={uploading || clientsLoading || creatingClient}
                  onClick={() => { void onRefreshClients({ rafraichir: true }); }}
                />
              </span>
            </div>

            <AppFormField
              className="mb-0 mt-16"
              label={pendingItems.length <= 1 ? 'Qui a fait le rendez-vous ?' : 'Personne pour les nouveaux fichiers'}
              htmlFor="depot-commercial"
              help="La personne qui a rencontré le client. C’est souvent vous."
            >
              <CommercialSelect
                id="depot-commercial"
                options={personnes.map((personne) => ({ value: personne.id, label: personne.nom }))}
                value={commercialId}
                onChange={onCommercialChange}
                disabled={uploading}
                allowEmpty
                placeholder="Tapez un nom…"
                emptyLabel="Personne introuvable"
              />
            </AppFormField>
          </DepotStep>

          <DepotStep number="2" title="Ajoutez ce que vous voulez envoyer">
            <div
              className={`depot-dropzone ${dragActive ? 'depot-dropzone--active' : ''} ${pendingItems.some((item) => item.kind === 'file') ? 'depot-dropzone--filled' : ''}`}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onClick={(event) => {
                if (event.target.closest('button')) return;
                onOpenFilePicker();
              }}
            >
              <Icon icon="mdi:folder-open-outline" className="depot-dropzone__icon" aria-hidden />
              <div className="depot-dropzone__copy">
                <p className="depot-dropzone__title mb-0">
                  {pendingItems.some((item) => item.kind === 'file')
                    ? 'Ajouter un autre fichier'
                    : 'Cliquez pour choisir un fichier'}
                </p>
                <p className="depot-dropzone__hint mb-0">
                  Enregistrement audio, PDF, Word… Vous pouvez aussi le glisser ici.
                </p>
              </div>
              <div className="depot-dropzone__actions">
                <AppButton
                  type="button"
                  icon="mdi:paperclip"
                  disabled={uploading || pendingItems.length >= MAX_FILES}
                  onClick={onOpenFilePicker}
                >
                  Choisir un fichier
                </AppButton>
                <AppButton
                  type="button"
                  variant="outline-secondary"
                  icon="mdi:notebook-edit-outline"
                  disabled={uploading || pendingItems.length >= MAX_FILES}
                  onClick={onAddNotes}
                >
                  Écrire des notes
                </AppButton>
              </div>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              className="d-none"
              multiple
              accept={acceptExtensions.map((ext) => `.${ext}`).join(',')}
              onChange={onFileInputChange}
            />
          </DepotStep>

          {pendingItems.length > 0 && (
            <div className="depot-file-list mb-16">
              <div className="depot-file-list__toolbar">
                <span className="text-sm fw-medium">
                  {pendingItems.length === 1
                    ? '1 élément à envoyer'
                    : `${pendingItems.length} éléments à envoyer`}
                  {pendingFileCount >= 2 ? ` · ${formatFileSize(pendingFilesBytes)}` : ''}
                </span>
                {showPerFileClient ? (
                  <div className="depot-file-list__actions">
                    <span className="depot-icon-tip depot-icon-tip--end" data-tooltip="Même client pour tous">
                      <AppButton
                        type="button"
                        variant="outline-secondary"
                        size="sm"
                        icon="mdi:office-building-outline"
                        iconWidth={18}
                        className="depot-icon-btn"
                        disabled={uploading || !clientFolderId}
                        aria-label="Même client pour tous"
                        onClick={onApplyDefaultClientToAll}
                      />
                    </span>
                    <span className="depot-icon-tip depot-icon-tip--end" data-tooltip="Même personne pour tous">
                      <AppButton
                        type="button"
                        variant="outline-secondary"
                        size="sm"
                        icon="mdi:account-multiple-check-outline"
                        iconWidth={18}
                        className="depot-icon-btn"
                        disabled={uploading || !commercialId}
                        aria-label="Même personne pour tous"
                        onClick={onApplyDefaultCommercialToAll}
                      />
                    </span>
                  </div>
                ) : null}
              </div>

              {pendingItems.map((item) => {
                const isDoc = item.typeDepot === 'document';
                const isNote = item.typeDepot === 'notes';
                const isAudio = item.typeDepot === 'audio_rdv';
                return (
                  <div key={item.id} className={`depot-file-row depot-file-row--${item.status}`}>
                    <div className="depot-file-row__main">
                      <Icon
                        icon={isNote ? 'mdi:notebook-edit-outline' : isDoc ? 'mdi:file-document-outline' : 'mdi:file-music-outline'}
                        className="depot-file-row__icon"
                        aria-hidden
                      />
                      <div className="depot-file-row__meta min-w-0">
                        <p className="depot-file-row__name mb-0" title={item.file?.name || item.objet || 'Notes'}>
                          {isNote ? (item.objet.trim() || 'Notes à mettre en forme') : item.file.name}
                        </p>
                        <p className="depot-file-row__size mb-0">
                          <DepotTypeBadge typeDepot={item.typeDepot} />
                          {item.file ? ` · ${formatFileSize(item.file.size)}` : null}
                          {item.dateFromFilename ? (
                            <span className="depot-file-row__detected"> · date trouvée dans le nom</span>
                          ) : null}
                          {item.clientFromFilename ? (
                            <span className="depot-file-row__detected"> · client trouvé dans le nom</span>
                          ) : null}
                        </p>
                      </div>
                      {!uploading && (
                        <span className="depot-icon-tip depot-icon-tip--end" data-tooltip="Retirer">
                          <AppButton
                            type="button"
                            variant="outline-secondary"
                            size="sm"
                            icon="mdi:close"
                            iconWidth={18}
                            className="depot-icon-btn depot-icon-btn--danger"
                            aria-label="Retirer"
                            onClick={() => onRemovePendingItem(item.id)}
                          />
                        </span>
                      )}
                    </div>

                    <div
                      className={[
                        'depot-file-row__fields',
                        isDoc ? 'depot-file-row__fields--doc' : '',
                        isAudio ? 'depot-file-row__fields--audio' : '',
                        isNote ? 'depot-file-row__fields--notes' : '',
                        showPerFileClient ? 'depot-file-row__fields--mixed' : '',
                      ].filter(Boolean).join(' ')}
                    >
                      {showPerFileClient && (
                        <div className="depot-file-row__client">
                          <RagClientSelect
                            clients={clients}
                            value={item.clientFolderId || ''}
                            onChange={(value) => {
                              onUpdatePendingItem(item.id, {
                                clientFolderId: value,
                                clientFromFilename: false,
                              });
                              void onApplyCommercialSuggestion(value, [item.id]);
                            }}
                            loading={clientsLoading}
                            disabled={uploading}
                            creating={creatingClient}
                            allowAll={false}
                            allowCreate
                            onCreate={(nom) => onCreateClient(nom, { itemId: item.id })}
                            label="Client"
                            placeholder="Client…"
                            getOptionValue={getDriveClientValue}
                            getOptionLabel={getDriveClientLabel}
                          />
                        </div>
                      )}
                      {showPerFileClient && (
                        <AppFormField className="mb-0 depot-file-row__commercial" label="Personne" htmlFor={`depot-commercial-${item.id}`}>
                          <CommercialSelect
                            id={`depot-commercial-${item.id}`}
                            options={personnes.map((personne) => ({ value: personne.id, label: personne.nom }))}
                            value={item.commercialId || ''}
                            onChange={(next) => onUpdatePendingItem(item.id, {
                              commercialId: next,
                              commercialSource: next ? 'manuel' : null,
                            })}
                            disabled={uploading}
                            allowEmpty
                            placeholder="Personne…"
                            emptyLabel="Personne introuvable"
                            buttonClassName="form-select form-select-sm text-start"
                          />
                        </AppFormField>
                      )}
                      {isAudio && (
                        <AppFormField className="mb-0" label="Type de rendez-vous" htmlFor={`depot-type-${item.id}`}>
                          <AppSelect
                            id={`depot-type-${item.id}`}
                            size="sm"
                            value={item.typeRdv || typeRdv}
                            disabled={uploading}
                            onChange={(event) => onUpdatePendingItem(item.id, { typeRdv: event.target.value })}
                          >
                            {TYPE_RDV_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </AppSelect>
                        </AppFormField>
                      )}
                      {isDoc && (
                        <AppFormField className="mb-0" label="Objet du document" htmlFor={`depot-objet-${item.id}`}>
                          <AppInput
                            id={`depot-objet-${item.id}`}
                            type="text"
                            size="sm"
                            value={item.objet}
                            disabled={uploading}
                            maxLength={255}
                            placeholder="Ex. contrat, bilan, présentation…"
                            onChange={(event) => onUpdatePendingItem(item.id, { objet: event.target.value })}
                          />
                        </AppFormField>
                      )}
                      <AppFormField
                        className="mb-0"
                        label={isAudio ? 'Date du rendez-vous' : 'Mois'}
                        htmlFor={`depot-date-${item.id}`}
                      >
                        <AppInput
                          id={`depot-date-${item.id}`}
                          type={isAudio ? 'date' : 'month'}
                          size="sm"
                          value={item.date}
                          disabled={uploading}
                          onChange={(event) => onUpdatePendingItem(item.id, {
                            date: event.target.value,
                            dateFromFilename: false,
                          })}
                        />
                      </AppFormField>
                    </div>

                    {isNote && (
                      <AppFormField className="mb-0 mt-8" label="Vos notes" htmlFor={`depot-notes-${item.id}`}>
                        <AppTextarea
                          id={`depot-notes-${item.id}`}
                          className="depot-notes-input"
                          rows={10}
                          value={item.texte}
                          disabled={uploading}
                          placeholder="Collez ici un mail, un brouillon ou des notes de réunion…"
                          onChange={(event) => onUpdatePendingItem(item.id, { texte: event.target.value })}
                        />
                      </AppFormField>
                    )}

                    {item.status === 'uploading' && (
                      <div className="progress depot-progress mt-8">
                        <div
                          className="progress-bar progress-bar-striped progress-bar-animated"
                          style={{ width: `${item.progress}%` }}
                        />
                      </div>
                    )}
                    {item.status === 'error' && item.error && (
                      <p className="depot-file-row__error mb-0">{item.error}</p>
                    )}
                    {item.status === 'done' && (
                      <p className="depot-file-row__ok mb-0">Envoyé</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <DepotStep number="3" title="Envoyez">
            {hasDocument ? (
              <AppCheckbox
                id="depot-extraction"
                wrapperClassName="depot-validation-check mb-12"
                checked={extractionRequise}
                onChange={(event) => onExtractionChange(event.target.checked)}
                disabled={uploading}
                label="Le document est une photo ou un scan : extraire le texte"
              />
            ) : null}

            {uploading && overallProgress !== null && (
              <div className="mb-12">
                <div className="d-flex justify-content-between text-sm mb-4">
                  <span>Envoi {uploadIndex}/{pendingItems.length}…</span>
                  <span>{overallProgress}%</span>
                </div>
                <div className="progress depot-progress">
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated"
                    style={{ width: `${overallProgress}%` }}
                  />
                </div>
              </div>
            )}

            {submitHint && !uploading ? (
              <p className="depot-submit-hint mb-8">{submitHint}</p>
            ) : null}

            <AppButton
              type="submit"
              className="w-100 depot-submit"
              icon="mdi:send"
              disabled={!canSubmit}
              loading={uploading}
              loadingLabel={`Envoi ${uploadIndex}/${pendingItems.length}…`}
            >
              {pendingItems.length > 1
                ? `Envoyer les ${pendingItems.length} éléments`
                : 'Envoyer'}
            </AppButton>
          </DepotStep>
        </form>
      </div>
    </div>
  );
}

export default DepotUploadForm;
